/**
 * DAM version numbering — pure helpers shared by every writer of
 * `datacrateModel/{reportType}/versions/vNNNN` (the `build:dam` CLI, the
 * admin `buildDam` action and the cold-start `buildDamInline` path).
 *
 * Version docs are immutable history: reports stamp `damVersionUsed` and the
 * lineage chip resolves that exact doc later. The next version therefore can
 * NOT be `pointer.version + 1` — after a rollback that restores the pointer
 * to v2 while v0003 already exists, that would overwrite v0003. The next
 * version is one past the HIGHEST version known from either the pointer or
 * the existing version doc ids. Writers additionally use create-only
 * semantics, so a race can fail a build but never overwrite a version.
 */

/** Zero-padded doc id (`v0001`, `v0042`), so lexicographic order matches
 *  numeric order up to 9999 versions per report type. */
export function damVersionDocId(version: number): string {
  return `v${String(version).padStart(4, '0')}`;
}

/** Parse a version doc id (`v0003` → 3). Returns null for ids that are not
 *  a positive integer version. */
export function parseDamVersionDocId(id: string): number | null {
  const match = /^v(\d+)$/.exec(id);
  if (!match) return null;
  const n = Number.parseInt(match[1]!, 10);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/**
 * Next DAM version = max(pointer version, highest existing version) + 1.
 * Non-integer or non-positive inputs are ignored; with no pointer and no
 * versions the first version is 1.
 */
export function resolveNextDamVersion(
  pointerVersion: number | null | undefined,
  existingVersions: Iterable<number>,
): number {
  let highest = 0;
  if (typeof pointerVersion === 'number' && Number.isSafeInteger(pointerVersion) && pointerVersion > highest) {
    highest = pointerVersion;
  }
  for (const v of existingVersions) {
    if (Number.isSafeInteger(v) && v > highest) highest = v;
  }
  return highest + 1;
}
